import { type SidebarPreferences, SidebarPreferencesSchema } from "@l30/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { apiFetch } from "./client.js";

/**
 * Sidebar UI preferences — favourites + collapsed sections.
 *
 * The mutation is optimistic: toggling a star / collapsing a section updates
 * the cache immediately and PUTs in the background. On error we revert to the
 * snapshot and surface a quiet toast; the sidebar stays usable.
 *
 * Writes are SERIALIZED via a shared mutation `scope` — the PUT replaces the
 * whole `sidebar` JSON, so two concurrent saves could otherwise reach the
 * server out of order and the older snapshot would clobber the newer one
 * (e.g. starring Contacts then Deals could persist only Contacts). Scoped
 * mutations run one-at-a-time in fire order; combined with each toggle reading
 * the freshest cache (so every PUT carries the cumulative state), the last
 * toggle always wins on the server.
 */
export const SIDEBAR_KEY = ["ui-preferences", "sidebar"] as const;

/** Shared scope id → all sidebar-pref writes run serially (no out-of-order clobber). */
const SAVE_SCOPE = "sidebar-preferences";

const EMPTY: SidebarPreferences = { favourites: [], collapsedSections: [] };

export function useSidebarPreferences() {
  return useQuery<SidebarPreferences>({
    queryKey: SIDEBAR_KEY,
    queryFn: () => apiFetch("/ui-preferences/sidebar", {}, SidebarPreferencesSchema),
    // Prefs are tiny + rarely change elsewhere; don't refetch on focus.
    staleTime: 5 * 60_000,
    placeholderData: EMPTY,
  });
}

export function useSaveSidebarPreferences() {
  const qc = useQueryClient();
  return useMutation<SidebarPreferences, Error, SidebarPreferences, { prev?: SidebarPreferences }>({
    // Serialize concurrent saves so out-of-order delivery can't clobber the
    // latest toggle (see header comment).
    scope: { id: SAVE_SCOPE },
    // Rolls back optimistically and shows its own single toast in onError;
    // opt out of the global MutationCache toast (U3) so a failed favourite
    // toggle never double-toasts.
    meta: { suppressGlobalError: true },
    mutationFn: (prefs) =>
      apiFetch(
        "/ui-preferences/sidebar",
        { method: "PUT", body: JSON.stringify(prefs) },
        SidebarPreferencesSchema,
      ),
    onMutate: async (next) => {
      await qc.cancelQueries({ queryKey: SIDEBAR_KEY });
      const prev = qc.getQueryData<SidebarPreferences>(SIDEBAR_KEY);
      qc.setQueryData<SidebarPreferences>(SIDEBAR_KEY, next);
      return { prev };
    },
    onError: (_err, _next, ctx) => {
      if (ctx?.prev) qc.setQueryData(SIDEBAR_KEY, ctx.prev);
      toast.error("Couldn't save your sidebar preference");
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: SIDEBAR_KEY });
    },
  });
}
